/**
 * localStorage persistence for one-click agents. Split out of oneClick.ts for
 * file size; oneClick.ts re-exports the public readers.
 */
import { ethers } from "ethers";
import { LocalStorageKeyEnum } from "../enums/localStorage";
import { AgentMap, PendingRevokeAgent, PendingRevokeMap, StoredAgent } from "./oneClick.types";

export const keyFor = (account: string, chainId: number) =>
  `${chainId}:${account.toLowerCase()}`;

export function loadAll(): AgentMap {
  try {
    const raw = localStorage.getItem(LocalStorageKeyEnum.ONE_CLICK_TRADING);
    return raw ? (JSON.parse(raw) as AgentMap) : {};
  } catch {
    return {};
  }
}

export function saveAll(map: AgentMap): void {
  try {
    localStorage.setItem(LocalStorageKeyEnum.ONE_CLICK_TRADING, JSON.stringify(map));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export const getStoredAgent = (account: string, chainId: number): StoredAgent | undefined =>
  loadAll()[keyFor(account, chainId)];

export function putStoredAgent(account: string, chainId: number, stored: StoredAgent): void {
  const map = loadAll();
  map[keyFor(account, chainId)] = stored;
  saveAll(map);
}

export function removeStoredAgent(account: string, chainId: number): void {
  const map = loadAll();
  delete map[keyFor(account, chainId)];
  saveAll(map);
}

/** Public (no private key) view of the stored agent for an account on a chain. */
export function getOneClickState(
  account: string | undefined | null,
  chainId: number | undefined | null
): { enabled: boolean; agentAddress: string; expiry: number } | null {
  if (!account || !chainId) return null;
  const stored = getStoredAgent(account, chainId);
  if (!stored) return null;
  return { enabled: stored.enabled, agentAddress: stored.agentAddress, expiry: stored.expiry };
}

/** True when one-click is enabled AND the agent approval has not expired. */
export function isOneClickActive(
  account: string | undefined | null,
  chainId: number | undefined | null
): boolean {
  const s = getOneClickState(account, chainId);
  return !!s && s.enabled && s.expiry > Math.floor(Date.now() / 1000);
}

/** The agent wallet to sign orders with, or null if one-click is not active. */
export function getAgentWallet(
  account: string | undefined | null,
  chainId: number | undefined | null
): ethers.Wallet | null {
  if (!account || !chainId) return null;
  const stored = getStoredAgent(account, chainId);
  if (!stored || !stored.enabled) return null;
  if (stored.expiry <= Math.floor(Date.now() / 1000)) return null;
  try {
    return new ethers.Wallet(stored.agentPrivateKey);
  } catch {
    return null;
  }
}

/* ---------------------------------------------------------------------------
 * Agents awaiting an on-chain revoke.
 *
 * Kept under their OWN localStorage key rather than on the StoredAgent, because
 * they have to outlive it: `enableOneClick` replaces the agent record wholesale
 * and a successful revoke deletes it. Nesting the list inside meant re-enabling
 * one-click after a failed revoke discarded the old agent's address entirely,
 * leaving a live on-chain approval with no UI path left to revoke it.
 * ------------------------------------------------------------------------- */

const nowSec = () => Math.floor(Date.now() / 1000);

const sameAddress = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function loadPendingMap(): PendingRevokeMap {
  try {
    const raw = localStorage.getItem(LocalStorageKeyEnum.ONE_CLICK_PENDING_REVOKE);
    return raw ? (JSON.parse(raw) as PendingRevokeMap) : {};
  } catch {
    return {};
  }
}

function savePendingMap(map: PendingRevokeMap): void {
  try {
    localStorage.setItem(
      LocalStorageKeyEnum.ONE_CLICK_PENDING_REVOKE,
      JSON.stringify(map)
    );
  } catch {
    /* ignore quota / private-mode errors */
  }
}

/**
 * An expired approval cannot sign, so there is nothing left to revoke.
 *
 * Array.isArray, not `list || []`: localStorage is user-writable and survives
 * downgrades, so the value under this key is not guaranteed to be the shape this
 * build wrote. A string there made `.filter` a TypeError, and because
 * `getPendingRevokes` runs on every render of the one-click toggle that was a
 * permanent white screen for that browser until site data was cleared. Each
 * entry is checked too -- one bad element must not take down the list.
 */
const unexpired = (list?: unknown): PendingRevokeAgent[] => {
  if (!Array.isArray(list)) return [];
  return list.filter(
    (p): p is PendingRevokeAgent =>
      !!p &&
      typeof p === "object" &&
      typeof (p as PendingRevokeAgent).agentAddress === "string" &&
      typeof (p as PendingRevokeAgent).expiry === "number" &&
      (p as PendingRevokeAgent).expiry > nowSec()
  );
};

export function getPendingRevokes(
  account: string | undefined | null,
  chainId: number | undefined | null
): PendingRevokeAgent[] {
  if (!account || !chainId) return [];
  return unexpired(loadPendingMap()[keyFor(account, chainId)]);
}

/** Idempotent: re-recording the same agent does not duplicate it. */
export function addPendingRevoke(
  account: string,
  chainId: number,
  agent: PendingRevokeAgent
): void {
  if (agent.expiry <= nowSec()) return;
  const map = loadPendingMap();
  const key = keyFor(account, chainId);
  const list = unexpired(map[key]);
  if (!list.some((p) => sameAddress(p.agentAddress, agent.agentAddress))) {
    list.push(agent);
  }
  map[key] = list;
  savePendingMap(map);
}

export function dropPendingRevoke(
  account: string,
  chainId: number,
  agentAddress: string
): void {
  const map = loadPendingMap();
  const key = keyFor(account, chainId);
  const list = unexpired(map[key]).filter(
    (p) => !sameAddress(p.agentAddress, agentAddress)
  );
  if (list.length) {
    map[key] = list;
  } else {
    delete map[key];
  }
  savePendingMap(map);
}
